import { PublicKey } from "@solana/web3.js";

// Network Configuration
export const SOLANA_NETWORK =
  (import.meta.env.VITE_SOLANA_NETWORK as "devnet" | "mainnet-beta") ||
  "devnet";
export const SOLANA_RPC_ENDPOINT =
  import.meta.env.VITE_SOLANA_RPC_ENDPOINT ||
  "https://api.devnet.solana.com";

// Mock Program IDs (placeholder addresses)
export const CLEARING_HOUSE_PROGRAM_ID = new PublicKey(
  "C1earHouseProgramXXXXXXXXXXXXXXXXXXXXXXXXXX"
);
export const PERP_MARKET_PROGRAM_ID = new PublicKey(
  "PerpMarketProgramXXXXXXXXXXXXXXXXXXXXXXXXXX"
);

// USDC Token Configuration
// Mainnet: EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v
// Devnet: 4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU
export const USDC_MINT_ADDRESS = new PublicKey(
  SOLANA_NETWORK === "mainnet-beta"
    ? "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"
    : "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU"
);
export const USDC_DECIMALS = 6;

// USDT Token Configuration
// Mainnet: Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB
// Devnet: No standard devnet USDT, using mainnet address
export const USDT_MINT_ADDRESS = new PublicKey(
  SOLANA_NETWORK === "mainnet-beta"
    ? "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB"
    : "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB"
);
export const USDT_DECIMALS = 6;

// Supported stablecoin tokens for deposits/withdrawals
export const SUPPORTED_TOKENS = {
  USDC: {
    symbol: "USDC",
    name: "USD Coin",
    mint: USDC_MINT_ADDRESS,
    decimals: USDC_DECIMALS,
  },
  USDT: {
    symbol: "USDT",
    name: "Tether",
    mint: USDT_MINT_ADDRESS,
    decimals: USDT_DECIMALS,
  },
} as const;

export type TokenSymbol = keyof typeof SUPPORTED_TOKENS;

// Market Configuration
export const MARKETS = {
  "BTC-USD": {
    symbol: "BTC-USD",
    baseSymbol: "BTC",
    quoteSymbol: "USD",
    binanceSymbol: "BTCUSDT",
    basePrecision: 5,
    quotePrecision: 1, // 1 decimal for BTC (tick size 0.1) - matches database
    minOrderSize: 0.0001,
    tickSize: 0.1,
  },
  "ETH-USD": {
    symbol: "ETH-USD",
    baseSymbol: "ETH",
    quoteSymbol: "USD",
    binanceSymbol: "ETHUSDT",
    basePrecision: 4,
    quotePrecision: 2,
    minOrderSize: 0.001,
    tickSize: 0.01,
  },
  "SOL-USD": {
    symbol: "SOL-USD",
    baseSymbol: "SOL",
    quoteSymbol: "USD",
    binanceSymbol: "SOLUSDT",
    basePrecision: 4,
    quotePrecision: 2,
    minOrderSize: 0.01,
    tickSize: 0.01,
  },
  "BNB-USD": {
    symbol: "BNB-USD",
    baseSymbol: "BNB",
    quoteSymbol: "USD",
    binanceSymbol: "BNBUSDT",
    basePrecision: 4,
    quotePrecision: 2,
    minOrderSize: 0.01,
    tickSize: 0.01,
  },
  "XRP-USD": {
    symbol: "XRP-USD",
    baseSymbol: "XRP",
    quoteSymbol: "USD",
    binanceSymbol: "XRPUSDT",
    basePrecision: 4,
    quotePrecision: 4,
    minOrderSize: 1,
    tickSize: 0.0001,
  },
  "ADA-USD": {
    symbol: "ADA-USD",
    baseSymbol: "ADA",
    quoteSymbol: "USD",
    binanceSymbol: "ADAUSDT",
    basePrecision: 4,
    quotePrecision: 4,
    minOrderSize: 1,
    tickSize: 0.0001,
  },
  "DOGE-USD": {
    symbol: "DOGE-USD",
    baseSymbol: "DOGE",
    quoteSymbol: "USD",
    binanceSymbol: "DOGEUSDT",
    basePrecision: 4,
    quotePrecision: 5,
    minOrderSize: 10,
    tickSize: 0.00001,
  },
  "MATIC-USD": {
    symbol: "MATIC-USD",
    baseSymbol: "MATIC",
    quoteSymbol: "USD",
    binanceSymbol: "POLUSDT",
    basePrecision: 4,
    quotePrecision: 4,
    minOrderSize: 1,
    tickSize: 0.0001,
  },
  "DOT-USD": {
    symbol: "DOT-USD",
    baseSymbol: "DOT",
    quoteSymbol: "USD",
    binanceSymbol: "DOTUSDT",
    basePrecision: 4,
    quotePrecision: 3,
    minOrderSize: 0.1,
    tickSize: 0.001,
  },
  "LTC-USD": {
    symbol: "LTC-USD",
    baseSymbol: "LTC",
    quoteSymbol: "USD",
    binanceSymbol: "LTCUSDT",
    basePrecision: 4,
    quotePrecision: 2,
    minOrderSize: 0.01,
    tickSize: 0.01,
  },
} as const;

export type MarketSymbol = keyof typeof MARKETS;

// Leverage Configuration
export const MIN_LEVERAGE = 1;

// ABSOLUTE_MAX_LEVERAGE is the technical ceiling for the entire system
// Actual per-market limits are configured in the admin panel and fetched from the backend
// Most markets are currently set to 20x max leverage
export const ABSOLUTE_MAX_LEVERAGE = 100;

// Default leverage when opening new positions
export const DEFAULT_LEVERAGE = 1;

// Funding Rate Configuration
export const FUNDING_RATE_INTERVAL_HOURS = 8;
export const MAX_FUNDING_RATE = 0.0005; // 0.05%
export const MIN_FUNDING_RATE = -0.0005; // -0.05%

// Margin Configuration
// Note: These are reference values. Actual risk management uses per-market
// leverage limits configured in the backend (currently 20x default, max 100x)
export const INITIAL_MARGIN_RATIO = 0.1; // 10% (for 10x reference)
export const MAINTENANCE_MARGIN_RATIO = 0.05; // 5%

